import React, { useMemo } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Helmet } from "react-helmet"
import uniq from "lodash.uniq"

import adTagMapper from "utils/adTagMapper"
import getIdealImage from "utils/getIdealImage"

const getMetadata = (article) => {
  const metadata = {
    title: article.titles.pageHeader || article.titles.site,
    // use canonical url if it is on the article object
    canonical: article.canonicalUrl || article.absoluteUrl,
    originalSource: article.canonicalUrl || article.absoluteUrl,
    // properties: article.metadata,
    nsfw: !!article.nsfw,
    restrictedUrlKeywords: false,
    facebook: {
      title: article.titles.social || article.titles.site,
      type: "article",
      image: article.useSocialPreview
        ? getIdealImage(
            article.previewImage,
            "c_fill,w_2000,h_1200,g_auto,fl_lossy,f_jpg",
            2000,
            1200,
            null,
            false,
          )
        : null,
      siteName: "mindbodygreen",
      publisher: "https://www.facebook.com/mindbodygreen",
      url: article.facebookUrl,
      tags: article.tags,
      appId: "6518106427",
    },
    twitter: {
      image: article.useSocialPreview
        ? getIdealImage(
            article.previewImage,
            "c_fill,w_800,h_400,g_auto,q_85,fl_lossy,f_jpg",
            800,
            400,
            null,
            false,
          )
        : null,
      site: "mindbodygreen",
      title: article.titles.social || article.titles.site,
      url: article.absoluteUrl,
    },
  }

  if (article.publishDate) {
    metadata.facebook.publishedTime = new Date(article.publishDate).toISOString()
  }

  if (
    article.lastUpdate &&
    article.publishDate &&
    new Date(article.publishDate) < new Date(article.lastUpdate)
  ) {
    metadata.facebook.lastModifiedTime = new Date(article.lastUpdate).toISOString()
  }

  metadata.facebook.description = article.socialPromotion?.caption || article.previewText
  if (article.socialPromotion?.tweetText) {
    // if we have tweetText, use it
    metadata.twitter.tweetText = article.socialPromotion.tweetText
  }
  metadata.twitter.description = metadata.facebook.description

  metadata.metaDescription = article.seo?.metaDescription || article.previewText
  metadata.metaKeywords = article.seo?.keywords || article.tags

  return metadata
}

const Metadata = ({ article }) => {
  const {
    id: articleId,
    elements,
    noIndex,
    titles: { pageHeader: title },
    authors,
    partner,
    marketingTags,
    category,
    subcategory,
    seo,
    noMBGMarketing,
  } = article

  const seoDescription = (() => {
    if (seo?.metaDescription) {
      return seo.metaDescription
    }
    if (elements[0] && elements[0].dek) {
      return elements[0].dek
    }
  })()

  const {
    canonical,
    originalSource,
    restrictedUrlKeywords,
    blockHeaderBidding,
    nsfw,
    facebook,
    twitter,
  } = getMetadata(article)

  const author = authors && authors[0]
  const facebookPage = (author && author.facebookPage) || (partner && partner.facebookPage)
  const twitterHandle = (author && author.twitter) || (partner && partner.twitter)

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `,
  )

  const adTags = useMemo(() => {
    const newTags = marketingTags
    newTags.push(...marketingTags.map((tag) => adTagMapper(tag)))
    if (category) {
      newTags.push(category.toLowerCase())
    }
    if (subcategory) {
      newTags.push(subcategory.toLowerCase())
    }
    return uniq(newTags.filter((tag) => Boolean(tag)))
  }, [marketingTags, category, subcategory])

  const trackonomicsTag = useMemo(() => {
    const tag = marketingTags.find((t) => t.match(/ecom\s?-/i))
    if (tag) {
      return tag
        .replace(/ecom\s?-\s?/i, "")
        .toLowerCase()
        .trim()
    }
  }, [marketingTags])

  const meta = useMemo(
    () => ({
      "description": seoDescription,
      "original-source": originalSource,
      "robots": noIndex ? "max-image-preview:large, noindex" : "max-image-preview:large, index",
      "og:title": facebook.title,
      "og:type": "article",
      "og:image": facebook.image,
      "og:image:width": 2000,
      "og:image:height": 1200,
      "og:site_name": "mindbodygreen",
      "og:description": facebook.description,
      "og:url": facebook.url,
      ...(trackonomicsTag && { trackonomics: trackonomicsTag }),
      "article:publisher": facebook.publisher,
      "article:published_time": facebook.publishedTime,
      ...(facebook.lastModifiedTime && { "article:modified_time": facebook.lastModifiedTime }),
      ...(facebookPage && { "article:author": facebookPage }),
      "fb:app_id": "6518106427",
      "twitter:card": twitter.image ? "summary_large_image" : "summary",
      ...(twitter.image && { "twitter:image": twitter.image }),
      "twitter:description": twitter.description,
      "twitter:url": twitter.url,
      "twitter:title": twitter.title,
      "twitter:site": "mindbodygreen",
      ...(twitterHandle && { "twitter:creator": twitterHandle }),
      "mbg.page": "article",
      "mbg.ads.pageId": `article-${articleId}`,
      "mbg.ads.tags": adTags.join(","),
      "mbg.ads.restrictedKeyword": restrictedUrlKeywords,
      "mbg.ads.blockHeaderBidding": blockHeaderBidding,
      ...(noMBGMarketing && { "mbg.nomarketing": "true" }),
      "mbg.article_id": articleId,
      ...(partner && { "mbg.partnerId": partner.id }),
      "mbg.nsfw": nsfw,
      ...(article.tags && {
        keywords: article.tags.filter((tag, idx, self) => self.indexOf(tag) === idx).join(","),
      }),
      ...(article.tags && { "article:tag": article.tags.filter((e) => e !== "") }),
      ...(article.authors &&
        article.authors[0] && {
          "article:author": [
            article.authors[0].facebookPage ||
              `https://www.mindbodygreen.com/wc/${article.authors[0].url}`,
          ],
        }),
      ...(article.marketingTags && {
        "growth.tag": article.marketingTags.filter((e) => e !== "").map((e) => e.toLowerCase()),
      }),
      ...((article.category || article.subcategory) && {
        "growth.category": [
          article.category && article.category.toLowerCase(),
          article.subcategory && article.subcategory.toLowerCase(),
        ],
      }),
    }),
    [article],
  )

  return (
    <Helmet
      htmlAttributes={{ lang: "en" }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        { rel: "canonical", href: canonical },
        {
          rel: "alternate",
          type: "application/rss+xml",
          href: "https://www.mindbodygreen.com/rss/featured.xml",
        },
      ]}
      // Since the meta data for array need to be multi meta tags,
      // the returned array will may be larger than the entry (meta) array.
      // For this reason the next traversal will be with the reduce()
      // DOCS: https://ogp.me/#array
      meta={Object.entries(meta).reduce((accum, [name, content]) => {
        if (Array.isArray(content)) {
          // Set creates a list of elements with unique values from an iterable element
          const uniqueContent = Array.from(new Set(content))

          const tagList = uniqueContent.map((e) => ({ name, content: e }))
          return [...accum, ...tagList]
        }
        return [...accum, { name, content }]
      }, [])}
    />
  )
}

export default React.memo(Metadata)
